/**
 * AdaptToJobModal — "Adapter mon CV à une offre" modal.
 *
 * Flow:
 *  input  → user pastes job offer
 *  running → Auto IA pipeline with step-by-step progress
 *  diff   → CvDiffViewer: accept/reject each AI change
 *  done   → success screen: version name + next-action CTAs
 */

import { useEffect, useRef, useState } from 'react';
import { useNotif } from '../../../contexts/NotifContext';
import {
  WandSparkles, CheckCircle2, Loader2, AlertCircle,
  FileText, Sparkles, ArrowRight,
  Briefcase, Search, X,
} from 'lucide-react';
import { Modal } from '../../ui/Modal';
import { Textarea } from '../../ui/textarea';
import { Label } from '../../ui/label';
import { CvDiffViewer } from '../CvDiffViewer';
import type { PipelineProgress } from '../../../hooks/useAutoIa';
import type { CVData, ParsedJobOffer } from '../../../types';
import type { TemplateMetaItem } from '../../../hooks/useCvState';
import { computePrescore } from '../../../lib/prescore';
import { useI18n } from '../../../contexts/I18nContext';

interface AdaptToJobModalProps {
  open: boolean;
  onClose: () => void;
  jobDescription: string;
  setJobDescription: (v: string) => void;
  isAutoIaRunning: boolean;
  pipelineProgress: PipelineProgress | null;
  onRunAutoIa: () => void;
  beforeData: CVData;
  generatedCvData: CVData | null;
  onApplyPartial: (merged: CVData) => void;
  /** Name of the newly created version (set after apply) */
  activeVersionName?: string;
  /** Clear the global pipelineProgress once the modal takes ownership of the result */
  onClearProgress?: () => void;
  /** Currently active/last selected job offer (for the indicator) */
  parsedJobOffer?: ParsedJobOffer | null;
  /** Les offres sauvegardées (pour afficher les 3 dernières) */
  jobOffers?: Array<{ id: string; title: string; company: string; rawText: string; dateAdded: string; parsedData?: ParsedJobOffer }>;
  /** Callback pour sauvegarder l'offre courante avant d'adapter */
  onSaveOffer?: (text: string, title?: string) => void;
  /** Compétences du CV actif — pour prescore local automatique */
  cvSkills?: string[];
  /** Entries linking CV versions to job offers (for score lookup) */
  cvxjobEntries?: Array<{ cvVersionId: string; jobOfferId: string; atsScore?: number }>;
  /** Currently active CV version ID */
  activeTemplate?: string;
  /** All CV version metadata (for showing which version has a score) */
  templateMeta?: TemplateMetaItem[];
  /** Auto-start the adaptation pipeline when the modal opens (e.g. coming from Offres modal) */
  autoRun?: boolean;
  /** Called once autoRun has been consumed so parent can reset the flag */
  onAutoRunConsumed?: () => void;
  /** Switch the active CV to a given version ID (for "other CV" score badge click) */
  onSwitchToCv?: (id: string) => void;
  /** Map of cover letters already generated by version id — used to toggle the
   *  "Générer la lettre" / "Voir la lettre" CTA and skip redundant generation. */
  generatedCoverLetterByVersionId?: Record<string, string>;
}

type Phase = 'input' | 'running' | 'diff' | 'done' | 'error';

export function AdaptToJobModal({
  open,
  onClose,
  jobDescription,
  setJobDescription,
  isAutoIaRunning,
  pipelineProgress,
  onRunAutoIa,
  beforeData,
  generatedCvData,
  onApplyPartial,
  activeVersionName,
  onClearProgress,
  parsedJobOffer,
  jobOffers,
  onSaveOffer,
  cvxjobEntries,
  activeTemplate,
  templateMeta,
  autoRun,
  onAutoRunConsumed,
  onSwitchToCv,
  cvSkills = [],
  generatedCoverLetterByVersionId,
}: AdaptToJobModalProps) {
  const { t } = useI18n();
  const { mark } = useNotif();
  const surfaceRef = useRef<HTMLDivElement | null>(null);
  const [phase, setPhase] = useState<Phase>('input');
  const [appliedCount, setAppliedCount] = useState(0);
  const [newVersionName, setNewVersionName] = useState('');
  const [isFetchingUrl, setIsFetchingUrl] = useState(false);
  const [selectedOfferId, setSelectedOfferId] = useState<string | null>(null);
  const pendingRunRef = useRef<string | null>(null);
  const [showAllOffers, setShowAllOffers] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');


  // Reset to input when modal opens fresh (not while running)
  useEffect(() => {
    if (open && phase !== 'running') {
      setPhase('input');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /**
   * Detect pipeline completion by watching isAutoIaRunning going true → false.
   *
   * runAutoIa() creates & applies the new version directly — it never sets
   * generatedCvData. So we can't wait on generatedCvData; instead we watch
   * isAutoIaRunning. When it drops to false:
   *  - successMessage present (and not an error) → done
   *  - otherwise → error
   *
   * generatedCvData is only used when the user manually triggers useCvGeneration
   * (pro-mode "Générer CV adapté" button) — in that case phase is never 'running'.
   */
  const wasRunningRef = useRef(false);
  useEffect(() => {
    if (phase === 'running') {
      wasRunningRef.current = true;
    }
    if (wasRunningRef.current && !isAutoIaRunning && phase === 'running') {
      wasRunningRef.current = false;
      const msg = pipelineProgress?.successMessage ?? '';
      const isActualSuccess = !!msg && !msg.startsWith('Erreur');
      onClearProgress?.();
      if (isActualSuccess) {
        setPhase('done');
      } else {
        setPhase('error');
      }
    }
  }, [isAutoIaRunning, phase, pipelineProgress, onClearProgress]);

  // Auto-run when the modal is opened from "Offres" modal (autoRun=true)
  const autoRunConsumedRef = useRef(false);
  useEffect(() => {
    if (open && autoRun && phase === 'input' && jobDescription.trim() && !autoRunConsumedRef.current) {
      autoRunConsumedRef.current = true;
      onAutoRunConsumed?.();
      // Small delay so the modal renders before starting the pipeline
      setTimeout(() => handleRun(), 80);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, autoRun, phase, jobDescription]);

  // Reset the guard when modal closes
  useEffect(() => {
    if (!open) autoRunConsumedRef.current = false;
  }, [open]);

  const handleRun = () => {
    // Sauvegarder l'offre si pas encore dans la liste
    onSaveOffer?.(jobDescription);
    setPhase('running');
    onRunAutoIa();
  };

  // Auto-trigger run when jobDescription is updated from an inline offer click
  useEffect(() => {
    if (pendingRunRef.current !== null && jobDescription === pendingRunRef.current) {
      pendingRunRef.current = null;
      handleRun();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobDescription]);

  /** Score du CV actif pour cette offre */
  function getScoreForOffer(offerId: string): number | null {
    if (!cvxjobEntries || !activeTemplate || offerId === '__current__') return null;
    const entry = cvxjobEntries.find(
      (e) => e.cvVersionId === activeTemplate && e.jobOfferId === offerId && e.atsScore !== undefined
    );
    return entry?.atsScore ?? null;
  }

  /** Meilleur score obtenu avec un CV différent du CV actif — retourne {score, cvVersionId} */
  function getBestOtherScore(offerId: string): { score: number; cvVersionId: string } | null {
    if (!cvxjobEntries || offerId === '__current__') return null;
    let best: { score: number; cvVersionId: string } | null = null;
    for (const e of cvxjobEntries) {
      if (e.cvVersionId !== activeTemplate && e.jobOfferId === offerId && e.atsScore !== undefined) {
        if (best === null || (e.atsScore ?? 0) > best.score) {
          best = { score: e.atsScore as number, cvVersionId: e.cvVersionId };
        }
      }
    }
    return best;
  }

  // Used by the manual diff-viewer path (pro-mode generate flow)
  const handleApply = (merged: CVData) => {
    onApplyPartial(merged);
    const before = beforeData;
    let count = 0;
    if (merged.personalInfo.summary !== before.personalInfo.summary) count++;
    if (merged.personalInfo.title !== before.personalInfo.title) count++;
    before.experience.forEach((exp, i) => {
      if (merged.experience[i]?.description !== exp.description) count++;
    });
    const beforeSkills = new Set(before.skills);
    const addedSkills = merged.skills.filter(s => !beforeSkills.has(s));
    if (addedSkills.length) count++;
    setAppliedCount(count);
    setNewVersionName(activeVersionName || 'Nouvelle version adaptée');
    setPhase('done');
  };

  // Capture version name when transitioning to done (auto-IA path)
  useEffect(() => {
    if (phase === 'done' && !newVersionName && activeVersionName) {
      setNewVersionName(activeVersionName);
    }
  }, [phase, activeVersionName, newVersionName]);

  const handleClose = () => {
    // If the pipeline just succeeded, fire notifications so the user sees
    // which ActionRail buttons now carry new, auto-generated results.
    // Without this, removing the "Next Steps" grid from the done phase
    // would leave the generated letter completely hidden.
    if (phase === 'done') {
      const rect = surfaceRef.current?.getBoundingClientRect() ?? null;
      // CV has been swapped live in the preview — still mark 'adapt' briefly
      // so the ActionRail button pulses and the icon flies out.
      mark('adapt', rect);
      if (activeTemplate && generatedCoverLetterByVersionId?.[activeTemplate]) {
        // Slight offset so the two flights don't overlap visually.
        window.setTimeout(() => mark('letter', rect), 120);
      }
    }
    onClearProgress?.();
    setPhase('input');
    setSelectedOfferId(null);
    setShowAllOffers(false);
    setSearchQuery('');
    onClose();
  };

  // During 'running', hide this modal entirely — the global PipelineProgressModal
  // takes over (same visual as pro mode). The parent keeps open=true so we can
  // reopen to the 'done'/'error' screen once the pipeline finishes.
  const modalOpen = open && phase !== 'running';

  return (
    <Modal
      open={modalOpen}
      onClose={handleClose}
      title={
        phase === 'done' ? t('adapt.titleDone') :
        phase === 'error' ? t('adapt.titleError') :
        phase === 'diff' ? t('adapt.titleDiff') :
        t('adapt.title')
      }
      size="xl"
    >
      {/* ── Phase: input ── */}
      {phase === 'input' && (
        <div className="space-y-4">
          {/* ── Offres récentes avec sélection + boutons inline ─────────────── */}
          {(() => {
            const allOffers = jobOffers ?? [];
            const sorted = [...allOffers].sort(
              (a, b) => new Date(b.dateAdded).getTime() - new Date(a.dateAdded).getTime()
            );

            // Virtual entry from parsedJobOffer if not already saved
            type OfferEntry = { id: string; title: string; company: string; rawText: string };
            let virtualEntry: OfferEntry | null = null;
            if (parsedJobOffer && (parsedJobOffer.titre || parsedJobOffer.entreprise)) {
              const alreadySaved = allOffers.some(
                (o) =>
                  o.title.trim().toLowerCase() === (parsedJobOffer.titre || '').trim().toLowerCase() &&
                  o.company.trim().toLowerCase() === (parsedJobOffer.entreprise || '').trim().toLowerCase()
              );
              if (!alreadySaved) {
                virtualEntry = {
                  id: '__current__',
                  title: parsedJobOffer.titre || 'Offre active',
                  company: parsedJobOffer.entreprise || '',
                  rawText: jobDescription,
                };
              }
            }

            const allItems: OfferEntry[] = virtualEntry ? [virtualEntry, ...sorted] : sorted;

            if (allItems.length === 0 && !jobDescription.trim()) return null;
            if (allItems.length === 0) return null;

            // Filtre de recherche
            const q = searchQuery.trim().toLowerCase();
            const filteredItems = q
              ? allItems.filter(
                  (o) => o.title.toLowerCase().includes(q) || o.company.toLowerCase().includes(q)
                )
              : showAllOffers
              ? allItems
              : allItems.slice(0, 3);

            return (
              <div className="space-y-2">
                {/* En-tête */}
                <div className="flex items-center justify-between">
                  <p className="text-xs text-white/40 font-medium uppercase tracking-wider">{t('adapt.recentOffers')}</p>
                  {allItems.length > 3 && !q && (
                    <button
                      type="button"
                      onClick={() => setShowAllOffers((v) => !v)}
                      className="text-xs text-white/35 hover:text-white/60 transition-colors"
                    >
                      {showAllOffers ? t('adapt.seeLess') : t('adapt.seeAll').replace('{n}', String(allItems.length))}
                    </button>
                  )}
                </div>

                {/* Barre de recherche — visible seulement si > 3 offres */}
                {allItems.length > 3 && (
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-white/25 pointer-events-none" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        if (e.target.value) setShowAllOffers(false);
                      }}
                      placeholder={t('adapt.searchPlaceholder').replace('{n}', String(allItems.length))}
                      className="w-full pl-8 pr-8 py-2 text-xs bg-white/5 border border-white/10 text-white/70 placeholder:text-white/30 rounded-xl focus:outline-none focus:border-blue-500/40 transition-colors"
                    />
                    {searchQuery && (
                      <button
                        type="button"
                        onClick={() => setSearchQuery('')}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
                      >
                        <X className="size-3.5" />
                      </button>
                    )}
                  </div>
                )}

                {/* Liste des offres */}
                <div className="flex flex-col gap-1.5 max-h-[45dvh] sm:max-h-64 overflow-y-auto pr-0.5">
                  {q && filteredItems.length === 0 ? (
                    <p className="text-xs text-white/30 text-center py-4">{t('adapt.noOfferFound').replace('{q}', searchQuery)}</p>
                  ) : filteredItems.map((item) => {
                    const activeScore = getScoreForOffer(item.id);
                    const otherScore = getBestOtherScore(item.id);
                    const isSelected = selectedOfferId === item.id;
                    // Prescore = calcul local instantané basé sur les compétences CV vs offre
                    const offerParsedData = item.id === '__current__'
                      ? parsedJobOffer
                      : jobOffers?.find((o) => o.id === item.id)?.parsedData;
                    const prescore = computePrescore(cvSkills, offerParsedData);

                    return (
                      <div
                        key={item.id}
                        onClick={() => {
                          // Clic sur la row = sélectionner + charger l'offre, SANS lancer l'adaptation
                          setSelectedOfferId(item.id);
                          setJobDescription(item.rawText);
                        }}
                        className={`flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-2.5 px-3 py-2.5 rounded-xl border transition-all cursor-pointer ${
                          isSelected
                            ? 'border-blue-500/40 bg-blue-500/10'
                            : 'border-white/10 bg-white/[0.02] hover:bg-white/[0.04] hover:border-white/20'
                        }`}
                      >
                          {/* Icône + titre + entreprise */}
                        <div className="flex items-start gap-2.5 flex-1 min-w-0">
                          <Briefcase className={`size-3.5 shrink-0 mt-0.5 ${isSelected ? 'text-blue-400' : 'text-white/25'}`} />
                          <div className="min-w-0 flex-1">
                            <p className={`text-xs font-semibold truncate leading-snug ${isSelected ? 'text-blue-300' : 'text-white/70'}`}>
                              {item.title}
                            </p>
                            {item.company && (
                              <p className={`text-[10px] truncate mt-0.5 ${isSelected ? 'text-blue-400/60' : 'text-white/30'}`}>
                                {item.company}
                              </p>
                            )}
                            {/* Scores inline sous le titre */}
                            <div
                              className="flex items-center gap-1 flex-wrap mt-1"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {prescore !== null && (
                                <span className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold border ${
                                  prescore >= 60
                                    ? 'text-teal-300/80 bg-teal-500/10 border-teal-500/20'
                                    : prescore >= 35
                                    ? 'text-white/40 bg-white/5 border-white/10'
                                    : 'text-white/25 bg-white/[0.03] border-white/[0.07]'
                                }`}>
                                  ~{prescore}%
                                </span>
                              )}
                              {activeScore !== null && (
                                <span className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold border ${
                                  activeScore >= 70
                                    ? 'text-emerald-300 bg-emerald-500/15 border-emerald-500/30'
                                    : activeScore >= 50
                                    ? 'text-amber-300 bg-amber-500/15 border-amber-500/30'
                                    : 'text-red-300 bg-red-500/15 border-red-500/30'
                                }`}>
                                  <CheckCircle2 className="size-2.5" />
                                  {activeScore}/100
                                </span>
                              )}
                              {activeScore === null && otherScore !== null && (() => {
                                const cvName = templateMeta?.find((t) => t.id === otherScore.cvVersionId)?.name;
                                return (
                                  <button
                                    type="button"
                                    onClick={() => onSwitchToCv?.(otherScore.cvVersionId)}
                                    className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold border text-blue-300/70 bg-blue-500/10 border-blue-500/20 hover:bg-blue-500/20 transition-colors"
                                  >
                                    <Sparkles className="size-2.5" />
                                    {otherScore.score}/100
                                    {cvName && <span className="hidden sm:inline text-blue-400/50 max-w-[60px] truncate">· {cvName}</span>}
                                    <ArrowRight className="size-2.5 opacity-60" />
                                  </button>
                                );
                              })()}
                            </div>
                          </div>
                        </div>

                        {/* Bouton Adapter — toujours à droite, stopPropagation */}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedOfferId(item.id);
                            pendingRunRef.current = item.rawText;
                            setJobDescription(item.rawText);
                          }}
                          className={`shrink-0 self-center flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] sm:text-xs font-semibold transition-colors ${
                            activeScore !== null
                              ? 'bg-white/5 text-white/50 hover:bg-blue-500/15 hover:text-blue-300 border border-white/10'
                              : 'bg-blue-600 text-white hover:bg-blue-500'
                          }`}
                        >
                          <WandSparkles className="size-3" />
                          <span className="hidden sm:inline">{activeScore !== null ? t('adapt.adaptBtnRe') : t('adapt.adaptBtnNew')}</span>
                          <span className="sm:hidden">{activeScore !== null ? '↺' : '→'}</span>
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* ── Coller / URL manuellement ────────────────────────────────────── */}
          <div className="space-y-2">
            <Label className="text-sm text-white/50">{t('adapt.orPasteManually')}</Label>
            {isFetchingUrl && (
              <div className="flex items-center gap-1.5 text-xs text-blue-400 py-1">
                <Loader2 className="size-3 animate-spin" /> {t('adapt.fetchingOffer')}
              </div>
            )}
            <Textarea
              value={jobDescription}
              onChange={(e) => {
                const val = e.target.value;
                setJobDescription(val);
                // Auto-fetch si URL collée
                const trimmed = val.trim();
                if (/^https?:\/\//i.test(trimmed) && !trimmed.includes('\n') && trimmed.length > 10) {
                  setIsFetchingUrl(true);
                  fetch('/api/job/fetch-url', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ url: trimmed }),
                  })
                    .then((r) => r.json())
                    .then((json: { text?: string; error?: string }) => {
                      if (json.text && json.text.trim().length > 50) {
                        setJobDescription(json.text.trim());
                      }
                    })
                    .catch(() => {})
                    .finally(() => setIsFetchingUrl(false));
                }
              }}
              placeholder={t('adapt.pasteOrUrlPlaceholder')}
              rows={4}
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30 resize-none text-sm"
            />
            <div className="flex items-center justify-between gap-3">
              {!jobDescription.trim() && (
                <p className="text-xs text-white/30 flex items-center gap-1.5">
                  <AlertCircle className="size-3.5 shrink-0" />
                  {t('adapt.selectOrPaste')}
                </p>
              )}
              <button
                type="button"
                onClick={handleRun}
                disabled={!jobDescription.trim()}
                className="ml-auto flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-lg shadow-blue-600/20"
              >
                <WandSparkles className="size-4" />
                {t('adapt.adaptBtn')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Phase: running ── */}
      {phase === 'running' && (
        <div className="space-y-5 py-2">
          <div className="flex items-center gap-3">
            <div className="size-10 rounded-full bg-blue-600/20 flex items-center justify-center shrink-0">
              <Loader2 className="size-5 text-blue-400 animate-spin" />
            </div>
            <div>
              <p className="text-sm font-semibold text-white">{t('adapt.runningTitle')}</p>
              <p className="text-xs text-white/40">{t('adapt.runningSubtitle')}</p>
            </div>
          </div>

          {pipelineProgress && (
            <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 space-y-3">
              {pipelineProgress.fallbackMessage ? (
                <div className="flex items-center gap-2 text-amber-300 text-sm">
                  <Loader2 className="size-4 animate-spin shrink-0" />
                  {pipelineProgress.fallbackMessage}
                </div>
              ) : (
                <ul className="space-y-2.5">
                  {pipelineProgress.labels.map((label, i) => (
                    <li key={i} className="flex items-center gap-3 text-sm">
                      {i < pipelineProgress.step ? (
                        <span className="size-5 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center shrink-0">
                          <CheckCircle2 className="size-3 text-emerald-400" />
                        </span>
                      ) : i === pipelineProgress.step ? (
                        <span className="size-5 rounded-full bg-blue-600/20 border border-blue-500/40 flex items-center justify-center shrink-0">
                          <Loader2 className="size-3 text-blue-400 animate-spin" />
                        </span>
                      ) : (
                        <span className="size-5 rounded-full border border-white/15 shrink-0" />
                      )}
                      <span className={i <= pipelineProgress.step ? 'text-white/90' : 'text-white/30'}>
                        {label}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Phase: error ── */}
      {phase === 'error' && (
        <div className="space-y-5 py-2">
          <div className="flex items-start gap-3 p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-300">
            <AlertCircle className="size-5 shrink-0 mt-0.5" />
            <div className="space-y-1">
              <p className="text-sm font-semibold">{t('adapt.errorTitle')}</p>
              <p className="text-xs text-red-300/70">
                {t('adapt.errorSubtitle')}
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setPhase('input')}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border border-white/15 text-white/70 hover:text-white hover:bg-white/5 transition-all"
            >
              {t('adapt.editOffer')}
            </button>
            <button
              type="button"
              onClick={handleRun}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold bg-blue-600 text-white hover:bg-blue-500 transition-all"
            >
              <Loader2 className="size-4" />
              {t('adapt.retry')}
            </button>
          </div>
        </div>
      )}

      {/* ── Phase: diff ── */}
      {phase === 'diff' && generatedCvData && (
        <CvDiffViewer
          before={beforeData}
          after={generatedCvData}
          onApply={handleApply}
          onCancel={() => setPhase('input')}
        />
      )}

      {/* ── Phase: done ── */}
      {/*
        Flux logique :
          1. bannière succès (le CV adapté a été créé)
          2. mention discrète de ce qui a été auto-généré en plus (lettre, etc.)
          3. UN SEUL CTA « Voir mon CV adapté » qui ferme la modale.
        Plus de grille de "next steps" (Lettre/Score/Traduire) qui rivalisait
        avec le CTA principal et faisait cliquer « Voir la lettre » par erreur.
        Plus non plus de question « Le résultat vous convient ? » : illogique
        à ce stade puisque l'utilisateur n'a même pas encore vu le document.
        Les autres actions restent accessibles via l'ActionRail après fermeture.
      */}
      {phase === 'done' && (() => {
        const hasLetter = !!(activeTemplate && generatedCoverLetterByVersionId?.[activeTemplate]);
        return (
        <div ref={surfaceRef} className="space-y-5 py-2">
          <div className="flex items-center gap-4 p-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/20">
            <div className="size-12 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0">
              <CheckCircle2 className="size-6 text-emerald-400" />
            </div>
            <div className="min-w-0">
              <p className="text-base font-bold text-white">{t('adapt.newVersionCreated')}</p>
              {newVersionName && (
                <p className="text-sm text-emerald-300/80 truncate mt-0.5">
                  {newVersionName}
                </p>
              )}
              {appliedCount > 0 && (
                <p className="text-xs text-white/40 mt-1">
                  {t('adapt.modificationsApplied').replace('{n}', String(appliedCount)).replace('{s}', appliedCount > 1 ? 's' : '')}
                </p>
              )}
            </div>
          </div>

          {hasLetter && (
            <p className="flex items-start gap-1.5 text-xs text-violet-300/80 px-1">
              <FileText className="size-3.5 shrink-0 mt-0.5" />
              {t('adapt.letterAlsoGenerated')}
            </p>
          )}

          <button
            type="button"
            onClick={handleClose}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold bg-blue-600 text-white hover:bg-blue-500 transition-all shadow-lg shadow-blue-600/20"
          >
            {t('adapt.viewAdaptedCv')}
            <ArrowRight className="size-4" />
          </button>
        </div>
        );
      })()}
    </Modal>
  );
}
